// src/YearTestPaper/Instruction.jsx

import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Box, Card, List, ListItemButton, ListItemText, Breadcrumbs, Link } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../component/Api/instance";
import { UserContext } from "../Context";
import Dialog from "../component/ui/Dialog";
import { PrimaryButton, SecondaryButton } from "../component/ui/Buttons";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionIcon from "@mui/icons-material/Description";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { styled } from "@mui/material/styles";

const InstructionPageContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  padding: "32px 0",
  backgroundColor: theme.palette.background.default,
}));

const SidebarCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "24px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
}));

const InstructionDetailsCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "40px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  minHeight: "450px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    padding: "24px",
  },
}));

const PaperItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'selected'
})(({ theme, selected }) => ({
  borderRadius: "14px",
  marginBottom: "8px",
  padding: "12px 16px",
  backgroundColor: selected ? `${theme.palette.primary.main}0D` : "transparent",
  border: `1px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
  color: selected ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: selected ? 700 : 500,
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: selected ? `${theme.palette.primary.main}1A` : theme.palette.action.hover,
  },
}));

export default function Instruction() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [data, setData] = useState([]);
  const [datas, setDatas] = useState("");
  const [paperid, setpaperid] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const { Endpoint } = useContext(UserContext);
  const email = localStorage.getItem("userMail");
  const { sno } = useParams();

  useEffect(() => {
    fetchAdData();
    fetchInstitutionData();
    fetchPaperOptions();
  }, [sno, email]);

  const fetchAdData = async () => {
    try {
      const response = await axiosInstance.post(`get/U_ViewAds.php`, {
        userId: email,
        category: "yearMCQ",
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
          return;
        }
        if (response.data && response.data.length > 0) {
          setDatas(response.data[0].attachment_file);
          setOpen(true); // Open the ad dialog once it is fetched
        }
      }
    } catch (error) {
      console.error("Error fetching ad data:", error);
    }
  };

  const fetchInstitutionData = async () => {
    try {
      const response = await axiosInstance.post(`get/U_ViewPmcqInstitution.php`, {
        userId: email,
        id: sno,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
          return;
        }
        const yearobj = response.data
          ?.map((item) => ({
            ...item,
            img: `${Endpoint}/controllers/api/admin/upload/${item.img}`,
          }))
          .filter((item) => item.sno == sno);
        setData(yearobj);
      }
    } catch (error) {
      console.error("Error fetching institution data:", error);
    }
  };

  const fetchPaperOptions = async () => {
    try {
      const response = await axiosInstance.post(`get/U_ViewPmcqPaper.php`, {
        id: sno,
        userId: email,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
          return;
        }
        const paperobj = response?.data?.filter(
          (item) => item.institution_id == sno
        );
        setOptions(paperobj);
      }
    } catch (error) {
      console.error("Error fetching paper options:", error);
    }
  };

  const handlePaperSelect = (index) => {
    setSelectedIndex(index);
    setpaperid(options[index].sno);
  };

  return (
    <InstructionPageContainer>
      <Container>
        {/* Custom Breadcrumb Nav */}
        <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: "24px" }}>
          <Link underline="hover" color="inherit" href="" onClick={(e) => { e.preventDefault(); navigate("/home"); }}>
            Home
          </Link>
          <Link underline="hover" color="inherit" href="" onClick={(e) => { e.preventDefault(); navigate("/year"); }}>
            Mock Exam Series
          </Link>
          <Typography color="text.primary">Instructions</Typography>
        </Breadcrumbs>

        {data.map((val) => (
          <Box key={val.sno}>
            {/* Header info */}
            <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px", flexWrap: "wrap", gap: "16px" }}>
              <Box>
                <Typography variant="h1" style={{ fontWeight: 800 }}>
                  {val.institution_name}
                </Typography>
                <Typography variant="body1" color="textSecondary" style={{ marginTop: "4px" }}>
                  {val.institution_desc}
                </Typography>
              </Box>
              {val.img && (
                <img
                  src={val.img}
                  alt="Institution Logo"
                  style={{ maxHeight: "65px", objectFit: "contain", borderRadius: "12px" }}
                />
              )}
            </Box>

            <Row className="g-4">
              {/* LEFT SIDEBAR: SELECT YEAR */}
              <Col xs={12} md={4} lg={3}>
                <SidebarCard>
                  <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", color: "var(--color-primary)" }}>
                    <CalendarMonthIcon />
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                      Select Exam Paper
                    </Typography>
                  </Box>

                  {options.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No paper options found.
                    </Typography>
                  ) : (
                    <List style={{ padding: 0 }}>
                      {options.map((option, index) => (
                        <PaperItemButton
                          key={index}
                          selected={selectedIndex === index}
                          onClick={() => handlePaperSelect(index)}
                        >
                          <ListItemText 
                            primary={`${option.year} - ${option.month}`} 
                            primaryTypographyProps={{ style: { fontWeight: selectedIndex === index ? 700 : 500, fontSize: "0.9rem" } }}
                          />
                        </PaperItemButton>
                      ))}
                    </List>
                  )}
                </SidebarCard>
              </Col>

              {/* RIGHT SIDEBAR: INSTRUCTIONS */}
              <Col xs={12} md={8} lg={9}>
                <InstructionDetailsCard>
                  <Box>
                    <Box style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
                      <DescriptionIcon color="primary" />
                      <Typography variant="h2" style={{ fontWeight: 800 }}>
                        Candidate Instructions
                      </Typography>
                    </Box>

                    <Box style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                      {val.institution_instruction
                        .split(/\r?\n/)
                        .filter((instruction) => instruction.trim() !== "")
                        .map((instruction, index) => (
                          <Box key={index} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                            <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "22px", height: "22px", borderRadius: "50%", backgroundColor: "rgba(30, 58, 138, 0.08)", color: "var(--color-primary)", fontWeight: 700, fontSize: "0.75rem", marginTop: "2px" }}>
                              {index + 1}
                            </Box>
                            <Typography variant="body1" color="textPrimary" style={{ lineHeight: 1.6 }}>
                              {instruction.trim()}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  </Box>

                  {/* Start Exam Action */}
                  <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: "40px" }}>
                    {selectedIndex !== null ? (
                      <PrimaryButton
                        onClick={() => navigate(`/test/${sno}/${paperid}`)}
                        endIcon={<ArrowForwardIcon />}
                        style={{ height: "46px", borderRadius: "14px", minWidth: "150px" }}
                      >
                        Start Exam
                      </PrimaryButton>
                    ) : (
                      <Box style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--color-text-secondary)" }}>
                        <HelpOutlineIcon fontSize="small" />
                        <Typography variant="body2">
                          Please select an exam paper from the left panel to begin.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </InstructionDetailsCard>
              </Col>
            </Row>

          </Box>
        ))}
      </Container>

      {/* ADS MODAL DIALOG */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Featured Announcement"
      >
        <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden", borderRadius: "16px", marginTop: "12px" }}>
          {datas && (
            <img
              src={`${Endpoint}/controllers/api/admin/upload/${datas}`}
              alt="Promotion banner"
              style={{ maxWidth: "100%", maxHeight: "400px", objectFit: "contain", borderRadius: "12px" }}
            />
          )}
        </Box>
        <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
          <SecondaryButton onClick={() => setOpen(false)}>
            Close
          </SecondaryButton>
        </Box>
      </Dialog>
    </InstructionPageContainer>
  );
}
